import { deepEqual, isNullValue } from '@fincity/kirun-js';
import React, {
	ChangeEvent,
	UIEvent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import ReactDOM from 'react-dom';
import CommonCheckbox from '../../commonComponents/CommonCheckbox';
import CommonInputText from '../../commonComponents/CommonInputText';
import {
	addListenerAndCallImmediately,
	getDataFromPath,
	getPathFromLocation,
	PageStoreExtractor,
	setData,
	UrlDetailsExtractor,
} from '../../context/StoreContext';
import { makeTempPath } from '../../context/TempStore';
import { Component, ComponentProps } from '../../types/common';
import { processComponentStylePseudoClasses } from '../../util/styleProcessor';
import { validate } from '../../util/validationProcessor';
import { SubHelperComponent } from '../HelperComponents/SubHelperComponent';
import { IconHelper } from '../util/IconHelper';
import { getRenderData, toDataTypeShape } from '../util/getRenderData';
import { getSelectedKeys } from '../util/getSelectedKeys';
import { findPropertyDefinitions } from '../util/lazyStylePropertyUtil';
import { runEvent } from '../util/runEvent';
import useDefinition from '../util/useDefinition';
import { flattenUUID } from '../util/uuid';
import DropdownStyle from './DropdownStyle';
import { propertiesDefinition, stylePropertiesDefinition } from './dropdownProperties';
import { styleDefaults, stylePropertiesForTheme } from './dropdownStyleProperties';
import { MOUSE_LEAVE_CLOSE_DELAY, openFloating } from '../util/floatingLayer';


type DropdownOption = { label: any; value: any; key: any; originalObjectKey?: any };

// Gap kept between an open list and the window edge, and the least height a capped list keeps.
const DROPDOWN_VIEWPORT_EDGE = 8;
const DROPDOWN_MIN_ROOM = 120;

function findOptionByValue(value: any, ...lists: Array<Array<DropdownOption | undefined>>) {
	for (const list of lists) {
		const found = list.find(e => deepEqual(e?.value, value));
		if (found) return found;
	}
	return undefined;
}

function DropdownComponent(props: Readonly<ComponentProps>) {
	const [showDropdown, setShowDropdown] = useState(false);
	const [searchDropdownData, setSearchDropdownData] = useState<
		Array<
			| {
					label: any;
					value: any;
					key: any;
					originalObjectKey: any;
			  }
			| undefined
		>
	>();
	const [selected, setSelected] = useState<any>();
	const [searchText, setSearchText] = useState('');
	const currentSearchText = useRef<string>(searchText);
	const [focus, setFocus] = useState(false);
	const [validationMessages, setValidationMessages] = React.useState<Array<string>>([]);
	const inputRef = useRef<HTMLInputElement>(null);
	const pageExtractor = PageStoreExtractor.getForContext(props.context.pageName);
	const urlExtractor = UrlDetailsExtractor.getForContext(props.context.pageName);
	const [hoverKey, setHoverKey] = useState<string | undefined>();
	const [isChanged, setIsChanged] = useState(false);
	const {
		definition: { bindingPath, bindingPath2 },
		locationHistory,
		context,
		definition,
		pageDefinition: { translations },
	} = props;
	const {
		key,
		properties: {
			labelKey,
			uniqueKey,
			selectionKey,
			labelKeyType,
			selectionType,
			uniqueKeyType,
			onClick,
			datatype,
			data,
			selectedData,
			placeholder,
			readOnly,
			label,
			closeOnMouseLeave,
			isMultiSelect,
			isSearchable,
			noFloat,
			validation,
			searchLabel,
			clearSearchTextOnClose,
			onSearch,
			runEventOnDropDownClose,
			onScrollReachedEnd,
			designType,
			colorScheme,
			leftIcon,
			clearOnSelectingSameValue,
			rightIcon,
			rightIconOpen,
			showMandatoryAsterisk,
			supportingText,
			showMultipleSelectedValues,
			removeKeyWhenEmpty,
			multiSelectNoSelectionValue,
			searchIcon,
			moveSelectedToTop,
			selectedOptionTickPlace,
			selectionOptionTickType,
			editRequestIcon,
			editConfirmIcon,
			editCancelIcon,
			analyticsLabel,
		} = {},
		stylePropertiesWithPseudoStates,
	} = useDefinition(
		definition,
		propertiesDefinition,
		stylePropertiesDefinition,
		locationHistory,
		pageExtractor,
		urlExtractor,
	);
	const clickEvent = onClick ? props.pageDefinition.eventFunctions?.[onClick] : undefined;
	const searchEvent = onSearch ? props.pageDefinition.eventFunctions?.[onSearch] : undefined;
	let bindingPathPath = getPathFromLocation(bindingPath!, locationHistory, pageExtractor);
	const searchBindingPath = getPathFromLocation(bindingPath2!, locationHistory, pageExtractor);

	const editOn = designType === '_editOnReq';

	const callClickEvent = useCallback(
		(force: boolean = false) => {
			if (!clickEvent || (editOn && !force)) return;
			(async () => {
				await runEvent(
					clickEvent,
					key,
					context.pageName,
					props.locationHistory,
					props.pageDefinition,
				);
			})();
		},
		[clickEvent, editOn, key, context.pageName, props.locationHistory],
	);

	const originalBindingPathPath = bindingPathPath;

	if (editOn && bindingPathPath) {
		bindingPathPath = makeTempPath(bindingPathPath, context.pageName);
	}

	// Return the remover: a Dropdown in a repeater row moves to another index when the
	// array is re-sorted, and a listener left on the old path keeps mirroring that row.
	useEffect(() => {
		if (!originalBindingPathPath) return;
		return addListenerAndCallImmediately(
			props.context.pageName,
			(_, value) => {
				setSelected(value);
			},
			originalBindingPathPath,
		);
	}, [originalBindingPathPath]);

	useEffect(() => {
		if (!searchBindingPath) return;
		return addListenerAndCallImmediately(
			props.context.pageName,
			(_, value) => {
				setSearchText(value ?? '');
			},
			searchBindingPath,
		);
	}, [searchBindingPath]);

	const dropdownData = React.useMemo(
		() =>
			Array.from(
				getRenderData(
					data,
					datatype,
					uniqueKeyType,
					uniqueKey,
					selectionType,
					selectionKey,
					labelKeyType,
					labelKey,
				)
					.reduce((acc: Map<string, any>, each: any) => {
						if (isNullValue(each?.key)) return acc;

						acc.set(each.key, each);

						return acc;
					}, new Map())
					.values(),
			),
		[
			data,
			datatype,
			uniqueKeyType,
			uniqueKey,
			selectionType,
			selectionKey,
			labelKeyType,
			labelKey,
		],
	);

	const selectedDataKey: Array<any> | string | undefined = React.useMemo(
		() => getSelectedKeys(dropdownData, selected, isMultiSelect),
		[selected, dropdownData, isMultiSelect],
	);

	// The options for the selection itself, supplied by the page, for a selection the
	// loaded data does not contain: an id bound before its row is in the first page, or
	// one a server search has filtered out. The shape of `data`, or one item of it, or
	// an array of items (see toDataTypeShape), since a fetch by id returns one item.
	const selectedDataOptions = React.useMemo(() => {
		const source = toDataTypeShape(selectedData, datatype);
		if (isNullValue(source)) return [];
		return getRenderData(
			source,
			datatype,
			uniqueKeyType,
			uniqueKey,
			selectionType,
			selectionKey,
			labelKeyType,
			labelKey,
		).filter(e => !isNullValue(e?.key));
	}, [
		selectedData,
		datatype,
		uniqueKeyType,
		uniqueKey,
		selectionType,
		selectionKey,
		labelKeyType,
		labelKey,
	]);

	// The label used to come from `data` alone, so the moment the list stopped holding
	// the selected row (a search, the next page, a reload of the first 20) the box went
	// blank although the bound value had not changed. The options last seen for the
	// selection are remembered and used when neither `data` nor `selectedData` has them.
	// Only the current selection is kept, so this never grows past it.
	const rememberedSelectedOptions = useRef<Array<DropdownOption>>([]);
	const selectedOptions: Array<DropdownOption> = React.useMemo(() => {
		if (isNullValue(selected)) return [];
		let values: Array<any> = [selected];
		if (isMultiSelect) values = Array.isArray(selected) ? selected : [];
		const found: Array<DropdownOption> = [];
		for (const value of values) {
			const option = findOptionByValue(
				value,
				dropdownData,
				selectedDataOptions,
				rememberedSelectedOptions.current,
			);
			if (option) found.push(option);
		}
		rememberedSelectedOptions.current = found;
		return found;
	}, [selected, dropdownData, selectedDataOptions, isMultiSelect]);

	const getIsSelected = (key: any) => {
		if (!isMultiSelect) return deepEqual(selectedDataKey, key);
		if (Array.isArray(selectedDataKey))
			return !!selectedDataKey.find((e: any) => deepEqual(e, key));
		return false;
	};

	const handleClick = async (each: { key: any; label: any; value: any } | undefined) => {
		if (!each || !bindingPathPath) return;

		if (isMultiSelect) {
			let newSelectionIndex = (selected ?? []).findIndex((e: any) =>
				deepEqual(e, each.value),
			);
			setIsChanged(true);
			let aValue =
				newSelectionIndex === -1
					? [...(selected ?? []), each.value]
					: selected.filter((_: any, i: number) => i !== newSelectionIndex);
			if (!aValue?.length && multiSelectNoSelectionValue !== 'EMPTY_ARRAY') {
				if (multiSelectNoSelectionValue === 'UNDEFINED') aValue = undefined;
				else if (multiSelectNoSelectionValue === 'NULL') aValue = null;
			}
			setData(bindingPathPath, aValue, context.pageName, removeKeyWhenEmpty);
			if (editOn) setSelected(aValue);

			if (!runEventOnDropDownClose) {
				callClickEvent();
			}
		} else {
			setData(
				bindingPathPath,
				deepEqual(selected, each.value) && clearOnSelectingSameValue
					? undefined
					: each.value,
				context?.pageName,
				removeKeyWhenEmpty,
			);
			if (editOn) setSelected(each.value);
			callClickEvent();
			handleClose();
		}
	};

	// The box is a controlled input off searchText, and searchText only follows the
	// store when bindingPath2 is set. Returning early without one froze the field at
	// '' — isSearchable rendered a search box that could not be typed in. The binding
	// is what makes the text readable from the page (and what an onSearch event needs),
	// so it stays authoritative when present; without one the local state is enough.
	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		if (searchBindingPath) setData(searchBindingPath, value, context.pageName);
		else setSearchText(value);
	};

	// Debounced server-side search. The ref remembers the text the event last ran for,
	// so the same text never searches twice.
	//
	// It used to be assigned the searchEvent OBJECT inside the runEvent argument list
	// (`currentSearchText.current = searchEvent`), against a ref declared as a string
	// and compared to searchText one line above. So after the first search the guard
	// could never be false again, and because the dependency list includes
	// locationHistory and props.pageDefinition -- whose identity changes on any
	// re-render of the page -- an unrelated re-render re-fired the search 500ms later
	// with text that had not changed. Every one of those refires re-fetched the option
	// list, which is what could drop a selected-but-unsearched row out of it.
	React.useEffect(() => {
		if (!onSearch) return;
		const timer = setTimeout(() => {
			if (searchText === currentSearchText.current) return;
			currentSearchText.current = searchText;
			(async () =>
				await runEvent(
					searchEvent,
					key,
					context.pageName,
					locationHistory,
					props.pageDefinition,
				))();
		}, 500);
		return () => clearTimeout(timer);
	}, [searchText, searchEvent, locationHistory, props.pageDefinition]);

	React.useEffect(() => {
		if (onSearch) return;
		let searchExpression: string | RegExp;
		try {
			searchExpression = new RegExp(searchText, 'i');
		} catch (error) {
			searchExpression = '';
		}
		setSearchDropdownData(
			dropdownData
				.filter(e => !isNullValue(e?.label))
				.filter(e => (e?.label + '').search(searchExpression) !== -1),
		);
	}, [searchText, dropdownData]);

	const [mouseIsInside, setMouseIsInside] = useState(false);
	const mouseIsInsideRef = useRef(false);

	const setMouseInside = useCallback((value: boolean) => {
		mouseIsInsideRef.current = value;
		setMouseIsInside(value);
	}, []);

	const [wrapperRect, setWrapperRect] = useState<{
		top: number;
		left: number;
		width: number;
		height: number;
	} | null>(null);

	const updateWrapperRect = useCallback(() => {
		const wrapper = inputRef.current?.closest('.compDropdown') as HTMLElement | null;
		if (!wrapper) return;
		const r = wrapper.getBoundingClientRect();
		setWrapperRect({ top: r.top, left: r.left, width: r.width, height: r.height });
	}, []);

	useLayoutEffect(() => {
		if (!showDropdown) {
			setWrapperRect(null);
			return;
		}
		updateWrapperRect();
		const handler = () => updateWrapperRect();
		window.addEventListener('scroll', handler, true);
		window.addEventListener('resize', handler);
		return () => {
			window.removeEventListener('scroll', handler, true);
			window.removeEventListener('resize', handler);
		};
	}, [showDropdown, updateWrapperRect]);

	const handleClose = useCallback(() => {
		if (!showDropdown) return;

		if (runEventOnDropDownClose && isMultiSelect && isChanged) {
			callClickEvent();
		}
		setShowDropdown(false);
		setFocus(false);
		inputRef?.current?.blur();
		clearSearchTextOnClose && setSearchText('');
		setIsChanged(false);
	}, [
		showDropdown,
		isChanged,
		setShowDropdown,
		setFocus,
		inputRef,
		clearSearchTextOnClose,
		setSearchText,
		callClickEvent,
		runEventOnDropDownClose,
	]);

	// closeOnMouseLeave used to shut the panel the instant the cursor left it, which
	// makes the control hostile to aim: the gap between the box and the panel, or a
	// short detour off the edge while scanning a long list, threw the selection away.
	// Leaving now only ARMS a close, and re-entering either the box or the panel
	// disarms it, so a brief wander costs nothing.
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const cancelPendingClose = useCallback(() => {
		if (closeTimerRef.current === undefined) return;
		clearTimeout(closeTimerRef.current);
		closeTimerRef.current = undefined;
	}, []);

	const schedulePendingClose = useCallback(() => {
		cancelPendingClose();
		closeTimerRef.current = setTimeout(() => {
			closeTimerRef.current = undefined;
			// The cursor may have come back after the timer was armed but before it
			// fired; the ref is the live answer, the state would be a frame stale.
			if (!mouseIsInsideRef.current) handleClose();
		}, MOUSE_LEAVE_CLOSE_DELAY);
	}, [cancelPendingClose, handleClose]);

	useEffect(() => cancelPendingClose, [cancelPendingClose]);

	// One floating panel at a time (util/floatingLayer): opening this list closes other
	// open dropdowns and popovers, but not a popover this dropdown sits inside.
	const portalRef = useRef<HTMLDivElement>(null);
	const handleCloseRef = useRef(handleClose);
	handleCloseRef.current = handleClose;
	useEffect(() => {
		if (!showDropdown) return;
		const anchor = () => inputRef.current?.closest('.comp.compDropdown');
		return openFloating({
			close: () => handleCloseRef.current(),
			contains: el => !!(anchor()?.contains(el) || portalRef.current?.contains(el)),
			anchor,
		});
	}, [showDropdown]);

	const getLabel = useCallback(() => {
		let label = '';
		if (selected == undefined || (Array.isArray(selected) && !selected.length)) {
			return '';
		}
		if (!isMultiSelect) {
			label = selectedOptions[0]?.label;
			if (!label && searchEvent) {
				label = selected?.label;
			}
			return label;
		}

		if (showMultipleSelectedValues) {
			return selectedOptions
				.map(e => e.label)
				.filter(e => !isNullValue(e))
				.join(', ');
		}

		// Counted off the selection, not off the options it matched: an id outside the
		// loaded page is still selected.
		const count = Array.isArray(selected) ? selected.length : 0;
		return `${count} Item${count > 1 ? 's' : ''}  selected`;
	}, [selected, selectedOptions, isMultiSelect, showMultipleSelectedValues, searchEvent]);
	const computedStyles = processComponentStylePseudoClasses(
		props.pageDefinition,
		{ focus, disabled: readOnly },
		stylePropertiesWithPseudoStates,
	);

	useEffect(() => {
		if (!validation?.length) return;

		const validationMessages = validate(
			props.definition,
			props.pageDefinition,
			validation,
			selected,
			locationHistory,
			pageExtractor,
		);
		setValidationMessages(validationMessages);

		setData(
			`Store.validations.${context.pageName}.${flattenUUID(definition.key)}`,
			validationMessages.length ? validationMessages : undefined,
			context.pageName,
			true,
		);
		return () =>
			setData(
				`Store.validations.${context.pageName}.${flattenUUID(definition.key)}`,
				undefined,
				context.pageName,
				true,
			);
	}, [selected, validation]);

	useEffect(() => {
		if (!showDropdown || closeOnMouseLeave) return;
		const closeFunction = () => {
			if (mouseIsInside) return;
			handleClose();
		};
		window.addEventListener('mousedown', closeFunction);
		return () => window.removeEventListener('mousedown', closeFunction);
	}, [mouseIsInside, showDropdown, searchText, handleClose, closeOnMouseLeave]);

	const scrollEndEvent =
		onScrollReachedEnd && props.pageDefinition.eventFunctions?.[onScrollReachedEnd]
			? async (e: UIEvent<HTMLDivElement>) => {
					const target = e.target as HTMLDivElement;

					if (Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) > 2)
						return;

					await runEvent(
						props.pageDefinition.eventFunctions?.[onScrollReachedEnd],
						key,
						context.pageName,
						locationHistory,
						props.pageDefinition,
					);
				}
			: undefined;

	const [isAtBottom, setIsAtBottom] = useState(false);
	// Height the list may use on the side it opened, set only when it would not fit there.
	const [roomHeight, setRoomHeight] = useState<number | undefined>(undefined);
	// The theme's list cap (max-height from the stylesheet), read before the inline room cap
	// replaces it: an inline max-height beats the stylesheet, so a long list used to grow to the
	// whole room below the field instead of stopping at the theme's height.
	const themeMaxHeight = useRef<string | undefined>(undefined);
	const sortOrder = useMemo(() => {
		if (!moveSelectedToTop) return undefined;
		return Array.isArray(selectedDataKey) ? [...selectedDataKey] : [selectedDataKey];
	}, [moveSelectedToTop, showDropdown]);

	// The list actually on screen, in ONE place. The panel and the arrow-key handler
	// each used to decide this for themselves and had drifted: the handler tested
	// `searchText` alone, without `&& !onSearch`. With an onSearch event the server owns
	// the filtering and searchDropdownData is never populated (the local filter effect
	// returns early), so the moment anything was typed the handler switched to an
	// undefined list, found no length, cleared the hover key and returned. Arrow-key
	// navigation was dead in every server-searched dropdown, which is all of the client
	// pickers. Sharing the decision is what stops it drifting again.
	const visibleOptions = useMemo(
		() =>
			searchDropdownData?.length || (searchText && !onSearch)
				? searchDropdownData
				: dropdownData,
		[searchDropdownData, searchText, onSearch, dropdownData],
	);

	let dropdownContainer = null;
	if (showDropdown && wrapperRect) {
		let options = visibleOptions;

		if (sortOrder && options?.length) {
			options = [...options].sort((a, b) => {
				let aIndex = sortOrder.indexOf(a.key);
				let bIndex = sortOrder.indexOf(b.key);
				if (aIndex === -1) aIndex = options!.length;
				if (bIndex === -1) bIndex = options!.length;
				return aIndex - bIndex;
			});
		}

		const dropdownPanel = (
			<div
				className={`_dropdownContainer ${isAtBottom ? '_atBottom' : ''}`}
				style={{
					pointerEvents: 'auto',
					...(computedStyles.dropDownContainer ?? {}),
					...(roomHeight !== undefined && {
						maxHeight:
							(computedStyles.dropDownContainer?.maxHeight ?? themeMaxHeight.current)
								? `min(${computedStyles.dropDownContainer?.maxHeight ?? themeMaxHeight.current}, ${roomHeight}px)`
								: `${roomHeight}px`,
						overflowY: 'auto',
					}),
				}}
				onScroll={scrollEndEvent}
				onMouseEnter={() => {
					setMouseInside(true);
					cancelPendingClose();
				}}
				onMouseLeave={() => {
					setMouseInside(false);
					if (closeOnMouseLeave) schedulePendingClose();
				}}
				ref={element => {
					if (!element || searchText) return;
					const parentRect = element.parentElement?.getBoundingClientRect();
					if (!parentRect) return;
					if (roomHeight === undefined) {
						const cap = window.getComputedStyle(element).maxHeight;
						themeMaxHeight.current = cap && cap !== 'none' ? cap : undefined;
					}
					// Open on the side with more room, and cap the list to that room. It used
					// to flip above whenever it did not fit below, however little room there
					// was above, and never limited its height: a long role list on an
					// Organization popup started 2000px above the top of the window.
					// scrollHeight is the full list height even once it is capped, so this
					// settles instead of flipping back and forth.
					// A px theme cap is as tall as the list ever gets, so judge the room by it.
					const capPx = themeMaxHeight.current?.endsWith('px')
						? Number.parseFloat(themeMaxHeight.current)
						: Number.NaN;
					const natural = Number.isNaN(capPx)
						? element.scrollHeight
						: Math.min(element.scrollHeight, capPx);
					const below = window.innerHeight - parentRect.bottom - DROPDOWN_VIEWPORT_EDGE;
					const above = parentRect.top - DROPDOWN_VIEWPORT_EDGE;
					const atBottom = natural > below && above > below;
					const room = Math.floor(atBottom ? above : below);
					setIsAtBottom(atBottom);
					setRoomHeight(natural > room ? Math.max(room, DROPDOWN_MIN_ROOM) : undefined);
				}}
			>
				<SubHelperComponent
					definition={props.definition}
					subComponentName="dropDownContainer"
				/>
				{isSearchable && (
					<div
						className="_dropdownSearchBoxContainer"
						style={computedStyles.dropdownSearchBoxContainer ?? {}}
					>
						<SubHelperComponent
							definition={props.definition}
							subComponentName="dropdownSearchBoxContainer"
						/>
						{searchIcon ? (
							<i
								className={`_dropdownSearchIcon ${searchIcon}`}
								style={computedStyles.dropDownSearchIcon ?? {}}
							/>
						) : undefined}
						<input
							className="_dropdownSearchBox"
							style={computedStyles.dropdownSearchBox ?? {}}
							value={searchText}
							placeholder={searchLabel}
							onChange={handleSearch}
							onMouseDown={e => {
								e.stopPropagation();
							}}
						/>
					</div>
				)}
				{options?.map(each => {
					const isOptionSelected = getIsSelected(each?.key);
					let tick = undefined;

					if (selectionOptionTickType === 'TICK' && isOptionSelected) {
						tick = (
							<i
								className="_dropdownCheckIcon"
								style={computedStyles.dropdownCheckIcon ?? {}}
							>
								<SubHelperComponent
									definition={props.definition}
									subComponentName="dropdownCheckIcon"
								/>
							</i>
						);
					} else if (
						selectionOptionTickType === 'CHECKBOX' ||
						selectionOptionTickType === 'RADIO'
					) {
						tick = (
							<CommonCheckbox
								isChecked={isOptionSelected}
								showAsRadio={selectionOptionTickType === 'RADIO'}
								styles={computedStyles.checkbox ?? {}}
								thumbStyles={computedStyles.thumb ?? {}}
							/>
						);
					}

					return (
						<div
							className={`_dropdownItem ${
								isOptionSelected ? '_selected' : ''
							} ${each.key === hoverKey ? '_hover' : ''} ${selectedOptionTickPlace}`} // because of default className the background-color is not changing on hover to user defined.
							style={computedStyles.dropdownItem ?? {}}
							key={each?.key}
							onMouseEnter={() => setHoverKey(each?.key)}
							onMouseDown={() => handleClick(each)}
							tabIndex={0}
							role="option"
							aria-selected={isOptionSelected}
						>
							<SubHelperComponent
								definition={props.definition}
								subComponentName="dropdownItem"
							/>
							<label
								style={computedStyles.dropdownItemLabel ?? {}}
								className="_dropdownItemLabel"
							>
								<SubHelperComponent
									definition={props.definition}
									subComponentName="dropdownItemLabel"
								/>
								{each?.label}
							</label>
							{tick}
						</div>
					);
				})}
			</div>
		);

		dropdownContainer = ReactDOM.createPortal(
			<div
				ref={portalRef}
				className={`comp compDropdown _dropdownPortal ${designType ?? ''} ${
					colorScheme ?? ''
				}`}
				style={{
					position: 'fixed',
					top: wrapperRect.top,
					left: wrapperRect.left,
					width: wrapperRect.width,
					height: wrapperRect.height,
					pointerEvents: 'none',
					zIndex: 9999,
					// This div only exists to anchor the panel and to carry the
					// comp/designType/colorScheme classes the panel's selectors need. Without
					// these, the theme's .comp.compDropdown box (background, border, padding,
					// shadow) paints an empty copy of the control over the real one, hiding the
					// selected label and the right icon.
					background: 'transparent',
					border: 'none',
					boxShadow: 'none',
					padding: 0,
				}}
			>
				{!closeOnMouseLeave && (
					<button
						type="button"
						aria-label="Close dropdown"
						tabIndex={-1}
						className="_dropdownBackdrop"
						style={{
							position: 'fixed',
							inset: 0,
							pointerEvents: 'auto',
							zIndex: 0,
							background: 'transparent',
							border: 'none',
							padding: 0,
							cursor: 'default',
						}}
						onMouseDown={e => {
							e.stopPropagation();
							handleClose();
						}}
					/>
				)}
				{dropdownPanel}
			</div>,
			document.body,
		);
	}

	return (
		<CommonInputText
			id={key}
			cssPrefix="comp compDropdown"
			noFloat={noFloat}
			readOnly={readOnly}
			value={getLabel()}
			label={editOn ? '' : label}
			translations={translations}
			rightIcon={
				showDropdown
					? (rightIconOpen ?? 'fa-solid fa-angle-up')
					: (rightIcon ?? 'fa-solid fa-angle-down')
			}
			handleRightIcon={() => {
				if (!showDropdown) {
					inputRef.current?.focus();
				} else {
					setFocus(false);
					setShowDropdown(false);
				}
			}}
			valueType="text"
			isPassword={false}
			placeholder={placeholder}
			hasFocusStyles={stylePropertiesWithPseudoStates?.focus}
			validationMessages={validationMessages}
			context={context}
			hideClearContentIcon={true}
			blurHandler={() => {
				if (mouseIsInside) return;
				setFocus(false);
				setShowDropdown(false);
			}}
			focusHandler={() => {
				setFocus(true);
				setShowDropdown(true);
			}}
			autoComplete="off"
			styles={computedStyles}
			inputRef={inputRef}
			definition={props.definition}
			designType={designType}
			colorScheme={colorScheme}
			leftIcon={leftIcon}
			//rightIcon = {rightIcon} 'fa-solid fa-angle-up'
			showDropdown={showDropdown}
			onMouseEnter={() => {
				setMouseInside(true);
				cancelPendingClose();
			}}
			onMouseLeave={() => {
				setMouseInside(false);
				if (closeOnMouseLeave) schedulePendingClose();
			}}
			showMandatoryAsterisk={
				!!(
					(validation ?? []).find(
						(e: any) => e.type === undefined || e.type === 'MANDATORY',
					) && showMandatoryAsterisk
				)
			}
			supportingText={supportingText}
			analyticsLabel={analyticsLabel}
			updDownHandler={e => {
				if (e.key.startsWith('Arrow')) {
					if (!showDropdown) setShowDropdown(true);
					const data = visibleOptions;
					if (!data?.length) {
						setHoverKey(undefined);
						return;
					}
					let index = data?.findIndex(e => e?.key === hoverKey);
					if (index === -1) index = e.key.endsWith('Up') ? data.length - 1 : 0;
					else index = e.key.endsWith('Up') ? index - 1 : index + 1;
					if (index < 0) index = data.length - 1;
					if (index >= data.length) index = 0;
					setHoverKey(data[index]?.key);
				} else if (e.key === 'Enter' && showDropdown && hoverKey) {
					handleClick(dropdownData.find(e => e.key === hoverKey)).then();
				} else if (e.key === 'Escape') {
					setHoverKey(undefined);
					setShowDropdown(false);
				}
			}}
			showEditRequest={editOn}
			editRequestIcon={editRequestIcon}
			editConfirmIcon={editConfirmIcon}
			editCancelIcon={editCancelIcon}
			onEditRequest={(editMode, canceled) => {
				if (editMode || !originalBindingPathPath) return;
				if (canceled) {
					setSelected(
						getDataFromPath(originalBindingPathPath, locationHistory, pageExtractor),
					);
				} else {
					setData(originalBindingPathPath, selected, context?.pageName);
					callClickEvent(true);
				}
			}}
		>
			{dropdownContainer}
		</CommonInputText>
	);
}

const { designType, colorScheme } = findPropertyDefinitions(
	propertiesDefinition,
	'designType',
	'colorScheme',
);

const component: Component = {
	order: 7,
	name: 'Dropdown',
	displayName: 'Dropdown',
	description: 'Dropdown component',
	component: DropdownComponent,
	styleComponent: DropdownStyle,
	styleDefaults: styleDefaults,
	propertyValidation: (): Array<string> => [],
	properties: propertiesDefinition,
	stylePseudoStates: ['hover', 'focus', 'disabled'],
	styleProperties: stylePropertiesDefinition,
	bindingPaths: {
		bindingPath: { name: 'Selection Binding' },
		bindingPath2: { name: 'Search Binding' },
	},
	defaultTemplate: {
		key: '',
		name: 'Dropdown',
		type: 'Dropdown',
		properties: {
			label: { value: 'Dropdown' },
		},
	},
	sections: [{ name: 'Dropdown', pageName: 'dropdown' }],
	propertiesForTheme: [designType, colorScheme],
	stylePropertiesForTheme: stylePropertiesForTheme,
	externalStylePropsForThemeJson: true,
};

export default component;
